import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'rc-slider';
import styles from './VideoCallActionsControls.module.scss';
import TimeInput from '../../../components/TimeInput/TimeInput';
import { Icon } from '../../../components/Icon';
import controlBgImage from './controlBgImage.svg';
import position from './position.svg';
import Waveform from '../../../components/Waveform/Waveform';
import FloatingContainer from '../VideoTrimmingControls/FloatingContainer';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../common/components/Spinner';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useCallToActionContext } from '../../../context/CallToActionContext';
import VideoService from '../../../service/VideoService';
import { ToastNotification } from '../../../components/Toast/Toast';
import { toast } from 'react-toastify';
import { getCurrentActions, isHexColor } from '../../../utils/CallToActionHelper';
import { CustomButton } from '../../../components/Button/Button';
import {
  INITIAL_ACTION_SETTINGS,
  POSITIONS,
  MIN_ACTION_DURATION,
  DEFAULT_ACTION_DURATION,
  STEP_FROM_ACTION_OFFSET,
} from '../../../constants/callToActions';
import { useVideoProgressContext } from '../../../context/VideoProgressContext';

const POSITION_TYPES = {
  start: 0,
  end: 1,
};

const VideoCallActionsControls = ({ storyId, videoDuration, waveform = null, className }) => {
  const {
    callActions,
    initialCallActions,
    selectedCallAction,
    changeCallActions,
    changeSelectedCallAction,
    callActionChanged,
    callActionRangeChanged,
    updateCallActionsRangeChanged,
    removeCallAction,
    updateCallActions,
    updateInitialCallActions,
    isNewCallToAction,
    isCallActionsChanged,
    setCallActions,
    setIsNewCallToAction,
    addNewCallAction,
    changeIsNewActionTimeline,
    updateCallActionsSeekValue,
    brandColors,
    availableFonts,
  } = useCallToActionContext();

  const { videoProgress } = useVideoProgressContext();

  // Action with the left or right slider selected
  const [typePosition, setTypePosition] = useState(POSITION_TYPES.start);

  const [showSeekTo, setShowSeekTo] = useState(false); // to show current cursor position
  const [pointerPosition, setPointerPosition] = useState(0); // to show current cursor position
  const [isRangeChanging, setIsRangeChanging] = useState(false); // to hide opacity cursor when changing

  // Show start or end time
  const [handlersPositions, setHandlersPositions] = useState([0, 1]);

  const [isDragActionActive, setIsDragActionActive] = useState(false);
  const [startDragPosition, setStartDragPosition] = useState(0); // [0, 1]

  const [orderOfChange, setOrderOfChange] = useState(0);

  const [initialDragOffset, setInitialDragOffset] = useState(0);
  const [isDragInvalid, setIsDragInvalid] = useState(false);

  const [temporarySeekValue, setTemporarySeekValue] = useState(null);
  const [isDragged, setIsDragged] = useState(false);
  const [callActionToMove, setCallActionToMove] = useState({});

  const getActionsRange = actions =>
    actions.map(({ id, offset, duration }) => ({ id, startTime: offset, endTime: offset + duration }));

  const getInsideAction = useCallback(
    actions => {
      const currentTime = videoProgress.percents * videoDuration || 0;
      const actionsRange = getActionsRange(actions);
      return actionsRange.find(({ startTime, endTime }) => currentTime >= startTime && currentTime < endTime);
    },
    [videoProgress, videoDuration]
  );

  const getAvailableDurations = useCallback(
    (actions, insideAction) => {
      const actionsRange = getActionsRange(actions);
      const currentTime = videoProgress.percents * videoDuration || 0;

      let beforeEnd = null;
      let beforeNext = null;

      // If the timestamp is inside the action, then the new action will be added
      // immediately after the current action if there is a minimum action duration available
      const durationBeforeVideoEnd = insideAction ? videoDuration - insideAction.endTime : videoDuration - currentTime;

      if (durationBeforeVideoEnd > MIN_ACTION_DURATION) {
        beforeEnd = Math.min(DEFAULT_ACTION_DURATION, durationBeforeVideoEnd);
        if (beforeEnd < DEFAULT_ACTION_DURATION) {
          beforeEnd = MIN_ACTION_DURATION;
        }
      }

      const isInvalidTimestamps = actionsRange.some(({ id, startTime, endTime }, index) => {
        // If the timestamp is inside the action, then the new action will be added
        // immediately after the current action if there is a minimum action duration available
        if (insideAction?.id === id) {
          const nextAction = actionsRange[index + 1];
          if (nextAction) {
            // After current action
            const isShorterThanMinimum = nextAction.startTime - endTime < MIN_ACTION_DURATION;
            return isShorterThanMinimum;
          }
        }
        const isShorterThanMinimum = startTime > currentTime && startTime - currentTime < MIN_ACTION_DURATION;
        return isShorterThanMinimum;
      });

      if (!isInvalidTimestamps) {
        const isShorterThanDefault = actionsRange.some(({ id, startTime, endTime }, index) => {
          if (insideAction?.id === id) {
            const nextAction = actionsRange[index + 1];
            if (nextAction) {
              return nextAction.startTime - endTime < DEFAULT_ACTION_DURATION;
            }
          }
          return startTime > currentTime && startTime - currentTime < DEFAULT_ACTION_DURATION;
        });

        beforeNext = isShorterThanDefault ? MIN_ACTION_DURATION : DEFAULT_ACTION_DURATION;
      }
      return { beforeEnd, beforeNext };
    },
    [videoDuration, videoProgress]
  );

  const startDragTimestamp = useRef();

  const currentStartTime = useMemo(() => callActions?.find(action => action.id === selectedCallAction.id)?.offset || 0, [
    callActions,
    selectedCallAction.id,
  ]);

  const currentEndTime = useMemo(
    () =>
      (callActions?.find(action => action.id === selectedCallAction.id)?.offset || 0) +
      (callActions?.find(action => action.id === selectedCallAction.id)?.duration || 1000),
    [callActions, selectedCallAction.id]
  );

  const { t } = useTranslation();

  const callActionRef = useRef(null);

  const handleAddNewAction = useCallback(() => {
    let callToActionsToOperate = structuredClone(callActions);
    if (isCallActionsChanged) {
      if (confirm(t('call_to_action.editor.confirm.switch_to_another_action'))) {
        setCallActions(initialCallActions);
        callToActionsToOperate = structuredClone(initialCallActions);
        callActionChanged(false);
        changeSelectedCallAction({});
      } else {
        return;
      }
    }
    const isFirstCta = callActions.length === 0;

    const insideAction = getInsideAction(callToActionsToOperate);

    const { beforeEnd, beforeNext } = getAvailableDurations(callToActionsToOperate, insideAction);
    if (!beforeEnd || !beforeNext) {
      ToastNotification({ message: t('call_to_action.editor.new_action_tooltip.incorrect_position') });
      return;
    }

    // If the timestamp is inside the action, then the new action will be added
    // immediately after the current action if there is a minimum action duration available
    const offset = insideAction ? insideAction.endTime + 1 : videoProgress.milliseconds || 0;

    const isLastAction = callActions.every(action => offset >= action.offset);
    const duration = isLastAction ? beforeEnd : beforeNext;

    const newAction = {
      ...(isFirstCta ? INITIAL_ACTION_SETTINGS : structuredClone(callActions).sort((a, b) => b.id - a.id)[0]),
      id: uuidv4(),
      offset,
      duration,
      url: null,
      text: '',
    };
    if (isFirstCta) {
      newAction.font = availableFonts[0];
      if (isHexColor(brandColors[0]?.color)) {
        newAction.bgColor = brandColors[0]?.color;
      } else {
        newAction.bgColor = 'none';
      }
      newAction.textColor = '#ffffff'; // Default text color, can be changed based on requirements
    }
    setIsNewCallToAction(true);
    addNewCallAction(newAction);
    callActionChanged(true);
    changeIsNewActionTimeline(true);

    // We move to the beginning of the new added action so that we can add another new action immediately
    updateCallActionsSeekValue((offset + STEP_FROM_ACTION_OFFSET) / videoDuration);
  }, [callActions, videoProgress, isCallActionsChanged, getAvailableDurations, getInsideAction]);

  const updateActionTimestamp = (id, startPosition, endPosition) => {
    const offset = startPosition * videoDuration;
    const duration = endPosition * videoDuration - offset;

    if (duration < MIN_ACTION_DURATION) {
      return;
    }

    callActionChanged(true);

    const sortedByStartTime = callActions.sort((a, b) => a.offset - b.offset);
    const currentIndex = sortedByStartTime.map(({ id }) => id).indexOf(id);

    const leftClosest = sortedByStartTime[currentIndex - 1];
    const rightClosest = sortedByStartTime[currentIndex + 1];

    if (leftClosest && leftClosest.offset + leftClosest.duration >= offset) {
      const newOffset = leftClosest.offset + leftClosest.duration + 1;
      changeCallActions(id, {
        offset: newOffset,
        duration: endPosition * videoDuration - newOffset,
        order: orderOfChange,
      });

      setOrderOfChange(prevState => prevState + 1);
      return;
    }

    if (rightClosest && offset + duration >= rightClosest.offset) {
      changeCallActions(id, { offset, duration: rightClosest.offset - offset - 1, order: orderOfChange });

      setOrderOfChange(prevState => prevState + 1);
      return;
    }

    changeCallActions(id, { offset, duration, order: orderOfChange });
    setOrderOfChange(prevState => prevState + 1);
    setIsRangeChanging(true);
  };

  const onSeekChange = useCallback(
    rangeValue => {
      const currentTime = rangeValue * videoDuration;
      const currentAction = callActions.find(({ offset, duration }) => offset <= currentTime && currentTime <= offset + duration);

      /**
       * After adding the drag functionality directly to the callAction range element,
       * we need to determine whether it was just a click and release of the mouse without moving it or not
       * If there was a normal mouse click, set a new CallActionsSeekValue.
       * If there was a callAction movement, we ignore setting the value for CallActionsSeekValue.
       * This is done to separate these two actions.
       */
      if (currentAction && !callActionRef.current.isHandler) {
        // This value will be set to updateCallActionsSeekValue() after releasing the mouse button if there was no zoom movement
        setTemporarySeekValue(rangeValue);
        startDragActive({ rangeValue, tempCallActionToMove: currentAction });
      } else {
        updateCallActionsSeekValue(rangeValue);
      }
    },
    [updateCallActionsSeekValue, callActions, videoDuration, isNewCallToAction, isCallActionsChanged, selectedCallAction]
  );

  const handleOnMouseMove = e => {
    const cursorPosition = e.clientX - callActionRef.current.offsetLeft - callActionRef.current.offsetParent.offsetLeft;
    setPointerPosition(cursorPosition - 6);
  };

  const handleMouseDown = e => {
    callActionRef.current.isHandler = Array.from(e?.target?.classList).indexOf('rc-slider-handle') >= 0;

    const tabIndex = e.target.tabIndex;

    if (tabIndex >= 0) {
      setTypePosition(tabIndex);
    }
  };

  const startDragActive = useCallback(
    ({ event, rangeValue, tempCallActionToMove }) => {
      const currentAction = callActions.find(({ id }) => id === tempCallActionToMove.id) || {};

      let continueDragAction = !isNewCallToAction || (isNewCallToAction && selectedCallAction.id === tempCallActionToMove.id);

      if (!continueDragAction && isNewCallToAction && selectedCallAction.id !== tempCallActionToMove.id) {
        if (confirm(t('call_to_action.editor.confirm.switch_to_another_action'))) {
          setCallActions(initialCallActions);

          updateCallActionsSeekValue(rangeValue);
          setIsNewCallToAction(false);
          changeSelectedCallAction(currentAction);
          callActionChanged(false);
        }
      }

      if (continueDragAction) {
        setIsDragActionActive(true);
        startDragTimestamp.current = Date.now();

        /**
         * CallAction movements are available in two ways:
         * 1) Via an icon outside the slider
         * In this case, we use the current position of the mouse
         * 2) By clicking on the callAction slider itself directly
         * In this case, we use the current range value
         * */
        setCallActionToMove(tempCallActionToMove);

        if (event) {
          setStartDragPosition(event.clientX - callActionRef.current.offsetLeft - callActionRef.current.offsetParent.offsetLeft);
          setIsDragged(true);
        }

        if (rangeValue) {
          setStartDragPosition(callActionRef.current.offsetWidth * rangeValue);
        }

        setInitialDragOffset(currentAction.offset);
        if (currentAction && !selectedCallAction?.id && !callActionRangeChanged) {
          changeSelectedCallAction(currentAction);
        }
        if (!currentAction) {
          changeSelectedCallAction({});
        }
      }
    },
    [isNewCallToAction, callActions, selectedCallAction, callActionRangeChanged]
  );

  const stopDragActive = () => {
    const dragThrottled = Date.now() - startDragTimestamp.current >= 150;

    setCallActionToMove({});
    setIsDragActionActive(false);
    setStartDragPosition(0);

    if (isDragInvalid) {
      changeCallActions(callActionToMove.id, { offset: initialDragOffset });
    } else {
      if (dragThrottled) {
        changeCallActions(callActionToMove.id, { order: orderOfChange });
        setOrderOfChange(prevState => prevState + 1);

        callActionChanged(true);
      }
    }

    if (!dragThrottled) {
      //There was no callAction movement - set temporarySeekValue
      const currentTime = temporarySeekValue * videoDuration;
      const currentAction = callActions.find(({ offset, duration }) => offset <= currentTime && currentTime <= offset + duration);

      // Change the selected action only if there was no movement of the action
      // and no action was selected
      if (selectedCallAction?.id && isCallActionsChanged && currentAction && selectedCallAction?.id !== currentAction.id) {
        if (confirm(t('call_to_action.editor.confirm.switch_to_another_action'))) {
          changeSelectedCallAction(currentAction);
          callActionChanged(false);
          updateCallActionsSeekValue(temporarySeekValue);
          changeCallActions(
            selectedCallAction.id,
            initialCallActions.find(item => item.id === selectedCallAction.id)
          );
        }
      } else {
        updateCallActionsSeekValue(temporarySeekValue);
        changeSelectedCallAction(currentAction);
      }
    }

    setIsDragged(false);
    setIsDragInvalid(false);
  };

  const actionDragListener = event => {
    if (callActionToMove.id === selectedCallAction?.id) {
      setIsDragged(true);

      const currentDragPosition = event.clientX - callActionRef.current.offsetLeft - callActionRef.current.offsetParent.offsetLeft;

      updateCallActionsRangeChanged(true);
      const minAvailableValue = 0;
      const maxAvailableValue = callActionRef.current.offsetWidth;

      const rangeInPercents = (currentDragPosition - startDragPosition) / maxAvailableValue;

      const { offset, duration } = callActions.find(action => action.id === callActionToMove.id) || {};

      let newOffset = offset + videoDuration * rangeInPercents;

      if (currentDragPosition <= minAvailableValue) {
        newOffset = 0;
      }

      if (currentDragPosition >= maxAvailableValue) {
        newOffset = videoDuration - duration;
      }

      if (newOffset < 0 || newOffset + duration > videoDuration) {
        return;
      }

      const actionsRange = getActionsRange(callActions.filter(action => action.id !== callActionToMove.id));

      const isInvalidTimestamps = actionsRange.some(({ startTime, endTime }) => {
        const isStartInside = startTime <= newOffset && newOffset <= endTime;
        const isEndInside = startTime <= newOffset + duration && newOffset + duration <= endTime;

        const isInsideAction = newOffset <= startTime && endTime <= newOffset + duration;

        return isStartInside || isEndInside || isInsideAction;
      });
      changeCallActions(callActionToMove.id, { offset: newOffset });

      setIsDragInvalid(isInvalidTimestamps);
    }
  };

  const handleDeleteAction = async () => {
    const isDeletionConfirmed = confirm(t('call_to_action.editor.delete_confirmation'));
    if (!isDeletionConfirmed) {
      return;
    }

    try {
      const actionIndex = initialCallActions.findIndex(({ id }) => id === selectedCallAction?.id);

      if (actionIndex >= 0) {
        const loadingToast = ToastNotification({
          message: t('call_to_action.editor.toast.deleting'),
          spinner: true,
        });

        toast.update(loadingToast, { autoClose: 10000 });
        await VideoService.removeStoryCallAction(selectedCallAction?.id);

        const response = await VideoService.getStoryCallActions(storyId);

        if (response) {
          const currentActions = getCurrentActions(response, videoDuration);
          updateCallActions(currentActions);
          updateInitialCallActions(currentActions);
        }

        toast.dismiss(loadingToast);
        ToastNotification({
          message: t('call_to_action.editor.toast.deleting_success'),
          iconName: 'success',
        });
      } else {
        removeCallAction(selectedCallAction?.id);
      }
      changeSelectedCallAction({});
      callActionChanged(false);
    } catch (err) {
      ToastNotification({
        message: t('call_to_action.editor.toast.deleting_error'),
      });
    }
  };

  const trackLeftRightBorder = useCallback(
    (actionId, actionText, actionUrl) => {
      if (isDragged && callActionToMove.id === actionId) {
        return isDragInvalid ? '1px solid red' : '1px solid #7C43AA';
      }

      if (selectedCallAction.id !== actionId) {
        return checkIsValid(actionId, actionText, actionUrl) ? '1px solid #7C43AA' : '1px solid red';
      }

      if (isDragged) {
        return '1px solid #7C43AA';
      }

      return 'none';
    },
    [selectedCallAction, callActionToMove, isDragInvalid, isDragged]
  );

  useEffect(() => {
    if (availableFonts.length && brandColors.length) {
      // Add new action if there is no actions exist
      if (callActions?.length == 0 && videoDuration) {
        // If the timestamp is inside the action, then the new action will be added
        // immediately after the current action if there is a minimum action duration available
        handleAddNewAction();
      }
    }
  }, [availableFonts, brandColors, videoDuration]);

  useEffect(() => {
    setHandlersPositions([currentStartTime / videoDuration, currentEndTime / videoDuration]);
  }, [callActions, currentStartTime, currentEndTime, videoDuration, selectedCallAction.id]);

  useEffect(() => {
    if (isDragActionActive) {
      document.addEventListener('mouseup', stopDragActive);
    }

    return async () => {
      document.removeEventListener('mouseup', stopDragActive);
    };
  }, [isDragInvalid, initialDragOffset, isDragActionActive, isDragged]);

  useEffect(() => {
    if (isDragActionActive) {
      document.addEventListener('mousemove', actionDragListener);
    }

    return () => document.removeEventListener('mousemove', actionDragListener);
  }, [isDragActionActive]);

  useEffect(() => {
    const body = document.querySelector('body');
    body.addEventListener('mousedown', handleMouseDown);

    return () => {
      body.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const checkURLFormat = url => {
    const re = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-z]{2,}(\/[^\/]+)*\/?$/gm;
    return re.test(url);
  };

  const checkIsValid = useCallback(
    (id, text, url) => {
      if (selectedCallAction.id !== id) {
        if (text.length > 0) {
          if (url === null) {
            return true;
          } else {
            return checkURLFormat(url.trim());
          }
        } else {
          return false;
        }
      } else {
        return true;
      }
    },
    [selectedCallAction]
  );

  return (
    <>
      <div className={styles.controlBtnContainer}>
        <CustomButton
          label={t('general.delete')}
          variant="secondary"
          onClick={handleDeleteAction}
          className={styles.deleteBtn}
          disabled={!selectedCallAction?.id}
        />
        <CustomButton label={t('text_box.editor.new_text_box')} variant="primary" onClick={handleAddNewAction} />
      </div>
      <div className={cx(styles.callActionsInstruments, className)} ref={callActionRef}>
        {showSeekTo && !isRangeChanging && !isDragged && (
          <div className={styles.cursor} style={{ left: `${pointerPosition}px` }}></div>
        )}
        <div
          className={styles.slidersWrapper}
          onMouseMove={handleOnMouseMove}
          onMouseOver={() => setShowSeekTo(true)}
          onMouseLeave={() => setShowSeekTo(false)}
        >
          {typePosition >= 0 && !!selectedCallAction.id && (
            <FloatingContainer
              className={styles.dragIconContainer}
              parentRef={callActionRef}
              floatingValue={handlersPositions[typePosition]}
              correction={typePosition ? -7 : -15}
            >
              <Icon
                src="dragIcon"
                className={cx(styles.dragIcon, {
                  [styles.startPosition]: typePosition === POSITION_TYPES.start,
                  [styles.endPosition]: typePosition === POSITION_TYPES.end,
                })}
              />
            </FloatingContainer>
          )}

          {!!selectedCallAction.id && (
            <FloatingContainer
              className={styles.dragAction}
              parentRef={callActionRef}
              floatingValue={
                handlersPositions[POSITION_TYPES.end] -
                (handlersPositions[POSITION_TYPES.end] - handlersPositions[POSITION_TYPES.start]) / 2
              }
              tooltip={t('call_to_action.editor.tooltip.drag')}
              disabled={isDragged}
            >
              <Icon
                src="dragAction"
                className={styles.dragIcon}
                onMouseDown={event => startDragActive({ event, tempCallActionToMove: selectedCallAction })}
              />
            </FloatingContainer>
          )}

          {callActions?.length > 0 ? (
            callActions.map((action, index) => (
              <Slider
                key={action?.id}
                range
                defaultValue={[0, 1]}
                value={[action.offset / videoDuration, (action.offset + action.duration) / videoDuration]}
                min={0}
                max={1}
                tabIndex={[0, 1]}
                step={1 / (videoDuration / 100)}
                allowCross={false}
                onChange={([startTime, endTime]) => updateActionTimestamp(action.id, startTime, endTime)}
                className={cx(styles.range, { [styles.draggableRange]: isDragged })}
                trackStyle={[
                  {
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    height: '56px',
                    border: 'none',
                    borderTop:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    borderBottom:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    borderLeft: trackLeftRightBorder(action.id, action.text, action.url),
                    borderRight: trackLeftRightBorder(action.id, action.text, action.url),
                    zIndex:
                      (callActionToMove.id === action.id && (isDragInvalid || isDragged)) ||
                        !checkIsValid(action.id, action.text, action.url)
                        ? 52
                        : 3,
                    ...(selectedCallAction.id === action.id && !isDragged && { borderRadius: '0' }),
                  },
                ]}
                handleStyle={[
                  {
                    display: selectedCallAction.id === action.id && !isDragged ? 'inline-block' : 'none',
                    width: '12px',
                    height: '56px',
                    backgroundColor:
                      selectedCallAction.id === action.id && typePosition === POSITION_TYPES.start ? '#7C43AA' : '#BDBDBD',
                    backgroundImage: `url(${controlBgImage})`,
                    backgroundPosition: '50% 50%',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0',
                    borderTopLeftRadius: '4px',
                    borderBottomLeftRadius: '4px',
                    border: 'none',
                    borderTop:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    borderLeft:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    borderBottom:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    boxShadow: 'none',
                    outline: 'none',
                    position: 'absolute',
                    marginLeft: '-12px', // 0px
                    zIndex: '51',
                    top: '10px',
                  },
                  {
                    display: selectedCallAction.id === action.id && !isDragged ? 'inline-block' : 'none',
                    width: '12px',
                    height: '56px',
                    backgroundColor:
                      selectedCallAction.id === action.id && typePosition === POSITION_TYPES.end ? '#7C43AA' : '#BDBDBD',
                    backgroundImage: `url(${controlBgImage})`,
                    backgroundPosition: '50% 50%',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: '0',
                    borderTopRightRadius: '4px',
                    borderBottomRightRadius: '4px',
                    border: 'none',
                    borderTop:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    borderRight:
                      (selectedCallAction.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    borderBottom:
                      (callActionToMove.id === action.id && isDragInvalid) || !checkIsValid(action.id, action.text, action.url)
                        ? '1px solid red'
                        : '1px solid #7C43AA',
                    boxShadow: 'none',
                    outline: 'none',
                    position: 'absolute',
                    top: '10px',
                    right: '0',
                    marginLeft: '0px', // -12px
                    zIndex: '51',
                  },
                ]}
                railStyle={{
                  height: '56px',
                  border: '1px solid #BDBDBD',
                  background: index === 0 ? '#EEEEEE' : 'none',
                  borderRadius: '4px',
                  boxShadow: 'none',
                }}
                onAfterChange={() => setIsRangeChanging(false)}
              />
            ))
          ) : (
            <Slider
              range
              defaultValue={[0, 1]}
              min={0}
              max={1}
              tabIndex={[0, 1]}
              step={1 / (videoDuration / 100)}
              allowCross={false}
              className={styles.range}
              trackStyle={[
                {
                  display: 'none',
                },
              ]}
              handleStyle={[
                {
                  display: 'none',
                },
                {
                  display: 'none',
                },
              ]}
              railStyle={{
                height: '56px',
                border: '1px solid #BDBDBD',
                background: '#EEEEEE',
                borderRadius: '4px',
                boxShadow: 'none',
              }}
            />
          )}

          <Slider
            className={styles.videoProgress}
            trackStyle={{
              backgroundColor: 'rgba(124, 67, 170, 0.1)',
              height: '56px',
              top: '30%',
              border: '0px',
              zIndex: 3,
            }}
            handleStyle={{
              backgroundImage: `url(${position})`,
              backgroundPosition: '50% 0%',
              borderRadius: '0',
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              cursor: 'default',
              backgroundRepeat: 'no-repeat',
              backgroundColor: 'transparent',
              zIndex: '50',
              height: '63px',
              top: '2px',
            }}
            railStyle={{
              height: '58px',
              background: 'transparent',
              zIndex: '50',
              boxShadow: 'none',
            }}
            min={0}
            max={1}
            step={0.001}
            onChange={onSeekChange}
            value={videoProgress.percents}
            included
          />

          {waveform ? (
            <div className={styles.waveformContainer}>
              <Waveform
                waveformData={waveform.map(item => item * 10000)}
                parentRef={callActionRef}
                trimStartPos={0}
                progress={videoProgress.percents}
              />
            </div>
          ) : (
            <Spinner width="30px" height="30px" size="3px" className={styles.spinnerIcon} />
          )}
          <FloatingContainer
            parentRef={callActionRef}
            floatingValue={videoProgress.percents}
            correction={8}
            className={styles.progressContainer}
          >
            <TimeInput
              inputClassName={styles.timeInput}
              disabled={true}
              value={(videoProgress.percents * videoDuration) / 1000}
              variant="basic"
              showControls={false}
            />
          </FloatingContainer>
          {typePosition >= 0 && !!selectedCallAction.id && (
            <FloatingContainer
              className={styles.callActionsContainer}
              parentRef={callActionRef}
              floatingValue={handlersPositions[typePosition]}
            >
              {typePosition === 0 ? (
                <TimeInput
                  inputClassName={styles.timeInput}
                  disabled={true}
                  value={currentStartTime / 1000}
                  variant="basic"
                  showControls={false}
                />
              ) : (
                <TimeInput
                  className={styles.timeInput}
                  disabled={true}
                  value={currentEndTime / 1000}
                  variant="basic"
                  showControls={false}
                />
              )}
              <div className={styles.keyHint}>
                <span>{t('my_video.tools.trim.use')}</span>
                <Icon src="keyArrowLeft" className={styles.keyIcon} />
                <Icon src="keyArrowRight" className={styles.keyIcon} />
              </div>
            </FloatingContainer>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoCallActionsControls;
